import { useState } from 'react';
import { usePost, CPLAT_API_ROUTES } from '@/api';
import { useToast } from '@/hooks/V3/useToast';
import { useAppSelector } from '@/redux/store';
import CoupangWingDialog from './CoupangWingDialog';
import type { InquiryItem } from '@/types/api';
import { cn } from '@/utils/tailwind';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import { useMembershipInquiryPreviewTutorial } from '@/components/membership/MembershipInquiryPreviewTutorial';
import AutoResizeTextarea from '../reviews/AutoResizeTextarea';
import AnswerController from './AnswerController';
import ExternalIcon from '@/assets/icons/OrderDetails/external-link-fill.svg?react';
import { useInquiry } from './InquiryProvider';
import moment from 'moment';

const RightContentSection = ({ inquiry }: { inquiry: InquiryItem }) => {
  const isAnswered = inquiry.CheckedYn === 'Y';
  const isPreviewMode = useMembershipPreviewMode();
  const { openTutorial } = useMembershipInquiryPreviewTutorial();
  const [comment, setComment] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [isCoupangDialogOpen, setIsCoupangDialogOpen] = useState(false);
  const [coupangWingError, setCoupangWingError] = useState(false);
  const [coupangInput, setCoupangInput] = useState('');
  const isUrgentInquiry = inquiry.QnaTypeCode === '긴급문의';
  const { refetchInquiryList } = useInquiry(); // 👈 요거 추가

  const user = useAppSelector((state) => state.user);

  const { mutate: registerInquiryComment, isPending } = usePost(
    CPLAT_API_ROUTES.registerInquiryComment,
  );

  const { showToast } = useToast();

  const MAX_LENGTH = 300;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (!isComposing && value.length > MAX_LENGTH) {
      setComment(value.slice(0, MAX_LENGTH));
    } else {
      setComment(value);
    }
  };

  // 쿠팡ID 입력/검증 다이얼로그
  const handleCoupangDialogConfirm = (wingId: string) => {
    // 실제 답변 등록 시도
    registerInquiryComment(
      {
        cplatToken: user.CplatToken,
        MarketId: Number(inquiry.MarketId),
        StoreId: inquiry.StoreId,
        inquiryNo: inquiry.InquiryNo,
        ProductNo: inquiry.ProductNo,
        AnswerContent: comment,
        QnaTypeCode: inquiry.QnaTypeCode,
        Cafe24Title: inquiry.Title,
        CoupangWingId: wingId,
        EsmToken: inquiry.EsmToken,
        LotteonProductItemNo: inquiry.ProductItemNo,
      },
      {
        onSuccess: (response) => {
          if (response.data.code == 200) {
            showToast('success', '답변이 등록되었습니다.');
            setComment('');
            setIsCoupangDialogOpen(false);
            setCoupangWingError(false);
            setCoupangInput('');
            refetchInquiryList();
          } else if (response.data.code == 766) {
            // 쿠팡ID 불일치
            setCoupangWingError(true);
            setIsCoupangDialogOpen(true);
          } else {
            showToast('warning', '답변 등록에 실패했습니다. 다시 시도해주세요.');
            setIsCoupangDialogOpen(false);
            setCoupangWingError(false);
            setCoupangInput('');
            refetchInquiryList();
          }
        },
        onError: () => {
          showToast('warning', '답변 등록 중 오류가 발생했습니다.');
        },
      },
    );
  };

  // 답변 등록 버튼 클릭
  const handleRegister = () => {
    const CoupangWingId = inquiry.CoupangWingId;
    if (!comment.trim()) return;
    if (inquiry.MarketNameEnglish == 'coupang' && !CoupangWingId) {
      setIsCoupangDialogOpen(true);
      return;
    }
    handleCoupangDialogConfirm(CoupangWingId || '');
  };

  const handleOpenSellerPage = () => {
    if (openTutorial('openSellerPage')) return;

    window.open(inquiry.SellerAdminPageUrl, '_blank');
  };

  if (isAnswered) {
    return <AnsweredSection inquiry={inquiry} />;
  }

  if (isUrgentInquiry) {
    return (
      <div className="flex h-full w-full items-center justify-end">
        <button
          className="flex items-center gap-[6px] rounded-[8px] border bg-gr-100 px-[10px] py-[6px] text-gr-700 Body5M14"
          onClick={handleOpenSellerPage}
          data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}
        >
          판매자 페이지
          <ExternalIcon className="text-gr-500" />
        </button>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          'bg flex h-full flex-col gap-2 rounded-[8px] border px-[10px] py-[12px]',
          'transition-all duration-200 ease-in-out  focus-within:border-point-400',
        )}
        data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}
      >
        <div className="flex h-full flex-col gap-2">
          <div className="w-full flex-1">
            {/* 답변 입력란 */}
            <AutoResizeTextarea
              value={comment}
              onChange={handleChange}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={(e) => {
                setIsComposing(false);
                if (e.currentTarget.value.length > MAX_LENGTH) {
                  setComment(e.currentTarget.value.slice(0, MAX_LENGTH));
                }
              }}
              placeholder="답변을 입력하세요"
            />
          </div>

          <AnswerController
            comment={comment}
            setComment={setComment}
            handleRegister={handleRegister}
            isLoading={isPending}
            inquiry={inquiry}
          />
        </div>
      </div>
      <CoupangWingDialog
        open={isCoupangDialogOpen}
        setOpen={(open) => {
          setIsCoupangDialogOpen(open);
          if (!open) {
            setCoupangInput('');
            setCoupangWingError(false);
          }
        }}
        onConfirm={handleCoupangDialogConfirm}
        error={coupangWingError}
        setError={setCoupangWingError}
        input={coupangInput}
        setInput={setCoupangInput}
      />
    </>
  );
};

export default RightContentSection;

const AnsweredSection = ({ inquiry }: { inquiry: InquiryItem }) => {
  return (
    <div
      className={cn(
        'flex h-full flex-col gap-[10px] rounded-sm border border-gr-200 bg-gr-50 px-2.5 py-3',
      )}
    >
      <div className="flex gap-2.5 text-gr-500 Caption1M12">
        <span>답글을 등록했어요</span>
        {moment(inquiry.InquiryCommentList?.[0]?.InquiryCommentDate).format('YY/MM/DD HH:mm')}
      </div>
      <div className="flex-1 text-gr-700 Body5M14">
        {inquiry.InquiryCommentList && inquiry.InquiryCommentList.length > 0 ? (
          inquiry.InquiryCommentList.map((item) => (
            <div key={item.InquiryCommentDate}>{item.InquiryComment}</div>
          ))
        ) : (
          <div className="text-gr-700 Body5M14">판매자 페이지에서 답변을 완료했어요</div>
        )}
      </div>
    </div>
  );
};
